import {
  FeatureFlag,
  useFeatureFlag,
  useFeatureFlagsReady,
} from "../contexts/launchDarklyContext";
import type { AnalyticsOwnerKind } from "../lib/analyticsOwner";
import { useSignedInWithOptro } from "./useSignedInWithOptro";

/**
 * Whether hybrid testing (Analytics columns) can be authored here, and if not,
 * whether the surface should be hidden or shown disabled with a reason.
 *
 * - `"hidden"`: the workspace is off the LaunchDarkly flags, or they have not
 *   resolved yet. Authoring surfaces are not rendered, except where an existing
 *   column has to stay reachable, which shows read-only and, once the flags
 *   have resolved, says why (the `*AuthoringOffCopy` helpers).
 * - `"signInRequired"`: the flags are on but the session did not sign in with
 *   Optro. Surfaces render disabled and say why
 *   (the `*_SIGN_IN_REQUIRED_COPY` strings below), the same split as Send to
 *   Optro: the flag decides whether the control exists, the sign-in whether it
 *   works.
 * - `"available"`: both hold.
 */
export type HybridTestingAvailability =
  | "hidden"
  | "signInRequired"
  | "available";

// Worded after Send to Optro's `SIGN_IN_REQUIRED_COPY`, one per surface.
export const ADD_ANALYTICS_COLUMN_SIGN_IN_REQUIRED_COPY =
  "Sign in with Optro to add an Analytics column.";
export const EDIT_ANALYTICS_COLUMN_SIGN_IN_REQUIRED_COPY =
  "Sign in with Optro to update an Analytics column.";
export const ATTACH_ANALYTICS_WORKFLOW_SIGN_IN_REQUIRED_COPY =
  "Sign in with Optro to attach or build an Analytics workflow for this column.";
export const ANALYTICS_INPUTS_SIGN_IN_REQUIRED_COPY =
  "Sign in with Optro to change Analytics inputs.";
// Generate stays enabled, so this one is only a tooltip on its button.
export const GENERATE_ANALYTICS_SIGN_IN_REQUIRED_COPY =
  "This workpaper has Analytics columns. Existing columns can still run; sign in with Optro to add or edit them.";

// Off the flags, for a surface that still shows an existing column. Signing in
// would not lift this hold, so where both apply these win over the sign-in copy.
// A work step names its own flag: the workspace may still author on controls.
// An `undefined` owner reads as a control, as in `useHybridTestingAvailability`.
const authoringOffClause = (ownerKind: AnalyticsOwnerKind | undefined) =>
  ownerKind === "workStep"
    ? "Analytics authoring on work steps is off for this workspace"
    : "Analytics authoring is off for this workspace";
// A column with no workflow bound has nothing to run.
export const editAnalyticsColumnAuthoringOffCopy = (
  ownerKind: AnalyticsOwnerKind | undefined,
  hasBinding: boolean,
) =>
  `${authoringOffClause(ownerKind)}, so this column can't be edited. ${hasBinding ? "It still runs, and it can be deleted." : "It can be deleted."}`;
export const reviewAnalyticsColumnsAuthoringOffCopy = (
  ownerKind: AnalyticsOwnerKind | undefined,
) =>
  `${authoringOffClause(ownerKind)}, so these columns can't be edited. Saved columns can be removed and proposed ones discarded.`;
export const runUnbuiltAnalyticsColumnAuthoringOffCopy = (
  ownerKind: AnalyticsOwnerKind | undefined,
) =>
  `This test is not connected to a published workflow, so there is nothing to run. ${authoringOffClause(ownerKind)}.`;

/**
 * Single source of truth for hybrid testing authoring: the Analytics tab and
 * choice in Add Column; the definition fields, attach, Build and drafting in
 * Edit Column; analytics columns in plan review; and the document-row
 * analytics-input and binding controls.
 *
 * Off the flags nobody authors or edits an analytics column definition: every
 * authoring surface is hidden or held read-only. Columns that already exist
 * are not hidden. Their results render, Generate keeps running them, and the
 * workpaper's existing copy and delete features still carry or remove them.
 * The backend refuses only a proposed analytics column in a plan, and its
 * agent drafts no plan column's test and suggests no workflow category; its
 * analytics routes, the dialog's draft-test included, take no flag, so a
 * person's own authoring and the dialog's drafting are held by these surfaces
 * alone. Off the flags, the input and binding columns are hidden too, existing
 * bindings included; without the Optro sign-in they stay readable and only
 * writes are held.
 *
 * `"hidden"` until flag values are ready for the signed-in workspace, and for
 * good when LaunchDarkly is unconfigured. A work step also needs
 * `work-step-hybrid-testing`; an `undefined` owner reads as a control.
 *
 * The sign-in is enforced in the frontend only: the backend runs analytics for
 * any session, so Generate is not held; its button only carries a tooltip
 * asking for the sign-in.
 */
export function useHybridTestingAvailability(
  ownerKind: AnalyticsOwnerKind | undefined,
): HybridTestingAvailability {
  const enabled = useFeatureFlag(FeatureFlag.hybridTesting);
  const workStepEnabled = useFeatureFlag(FeatureFlag.workStepHybridTesting);
  const ready = useFeatureFlagsReady();
  const signedInWithOptro = useSignedInWithOptro();
  const ownerEnabled = ownerKind === "workStep" ? workStepEnabled : true;
  if (!(enabled && ownerEnabled && ready)) return "hidden";
  return signedInWithOptro ? "available" : "signInRequired";
}

export interface HybridTestingAuthoring {
  availability: HybridTestingAvailability;
  /**
   * The resolved flags hold authoring off. Narrower than `"hidden"`, which
   * also covers flags still loading: a surface holds itself on `"hidden"` but
   * says authoring is off only on this.
   */
  authoringOff: boolean;
}

/** `useHybridTestingAvailability`, plus whether a surface may say authoring is off. */
export function useHybridTestingAuthoring(
  ownerKind: AnalyticsOwnerKind | undefined,
): HybridTestingAuthoring {
  const availability = useHybridTestingAvailability(ownerKind);
  const ready = useFeatureFlagsReady();
  return { availability, authoringOff: availability === "hidden" && ready };
}
